import _ from 'underscore';
import {XmlTagInfo} from "../xmlTagProvider";
import {ExpressionParser} from "./expression";
import {QuestionaryState} from "../questionary";
import {Form} from "../form/form";

export interface QuotaList {
	isQuotaFull: {(state: QuestionaryState, currentForm: Form): boolean};
    isCellFull: {(quotaName: string, cellName: string, state: any): boolean};
    getFullQuota: {(): null|{variable: QuotaVariable, cell: QuotaCell}};
}

interface QuotaCell {
	id: number,
	type: string,
    text?: string,
	condition: ((state: QuestionaryState) => boolean)|undefined, // if there is no condition it will be undefined
}

interface QuotaVariable {
	id: string,
	name: string,
	hard: boolean,
	cells: QuotaCell[],
    evaluateOnForm?: string,
}

let fullQuota: null|{variable: QuotaVariable, cell: QuotaCell} = null;

const quotaTagHandlers : { [xpath:string]: { (node: XmlTagInfo): any } } = {

	'quotaList': (node: XmlTagInfo):QuotaList => {
		const quotaVariables: QuotaVariable[] = node.children;
		return <QuotaList>{
			tag: 'quotaList',
            getFullQuota: () => {
			    return fullQuota;
            },
			isQuotaFull: (state: QuestionaryState, form?: Form) => {
                fullQuota = null;
				return _.reduce(quotaVariables, (result:boolean, v:QuotaVariable) => {
                    // When one quota full, no need to resolve others
                    if(result){ return result; }

                    if (v.evaluateOnForm && v.evaluateOnForm !== form?.id) {
                        console.log('SKIP QUOTA VARIABLE', v);
                        // this quota variable is not be evaluated on this form
                        return false;
                    }

                    const fnCellValue = (c:QuotaCell): boolean|undefined => {
                        try {
                            return (!c.condition) || c.condition(state);
                        } catch (err) {
                            console.error(err.message, err.stack);
                            throw new Error(`Unable to evaluate quota id ${c.id} (${c.type}, ${c.text}).`)
                        }
                    }

					const isCellFull = (c:QuotaCell) => state.quotas.cells[c.id - 1] === 0;

					const normalCells = v.cells.filter(c => c.type === 'normal');
                    if (normalCells.length === 0) {
                        // Quota has no cells => it can not be full
                        return false;
                    }

                    const normalCellsValues = normalCells.map(fnCellValue);

                    // Quota is decidable when all cells have value (true|false), or there is at least one fulfilled (true)
                    const decidable = normalCellsValues.some(v => v) || normalCellsValues.every(v => typeof v === 'boolean');
                    if (!decidable) {
                        // Quota cannot be decided yet, return false
                        return false;
                    }

					const normalCellsActive = normalCells.filter((c,i) => normalCellsValues[i]);
					const complement: QuotaCell = v.cells.filter(c => c.type === 'complement')[0];
					switch( normalCellsActive.length ) {
						case 0: {
                            // No active cell -> fall to complement
                            const result = isCellFull(complement);
                            if (result) {
                                fullQuota = {variable: v, cell: complement};
                            }
                            return result;
                        }
						case 1: {
                            // One active cell -> check it and fall to complement when full
                            const cell = normalCellsActive[0];
                            const result = isCellFull(cell) ? isCellFull(complement) : false;
                            if (result) {
                                fullQuota = {variable: v, cell: cell};
                            }
                            return result;
                        }
						default:
							// TODO error event - questionary fits to more then one quota cell
							console.warn("QUOTA_ERROR: multiple quota variable cells matched", normalCellsActive);
							return false;
					}
				}, false);
			},
            isCellFull: (quotaName, cellText, state) => {
                const quotaVariable = quotaVariables.filter(q => q.name === quotaName)[0];
                const cell = quotaVariable?.cells.filter(c => c.text === cellText)[0];
                if (!cell) {
                    return false;
                }
                return state.quotas.cells[cell.id - 1] === 0;
            },
		};
	},

	'quotaVariable': (node: XmlTagInfo):QuotaVariable => {
		return {
			id: node.attributes['id'],
			name: node.attributes['name'],
			hard: node.attributes['hard'] === "yes",
			cells: node.children,
            evaluateOnForm: node.attributes['evaluateOnForm'],
		};
	},

	'quotaVariable//cell': (node: XmlTagInfo):QuotaCell => {
		return {
			type: node.attributes['type'],
			id: Number(node.attributes['cellId']),
			condition: ExpressionParser.parseQuota(node),
            text: node.children.filter(n => n.tag === 'text')[0]?.value,
		};
	},

};

export default quotaTagHandlers;
